import { queryOptions } from '@tanstack/react-query';
import { ApiError } from '~/components/Providers/APIClient';
import {
    ClanProfile,
    ClanProfileMerged,
    CountryProfile,
    CountryProfileMerged,
    Profile,
    ProfileMerged,
} from '../../types/profiles';
import {
    CountryClanProfileMergedLevel,
    ProfileMergedLevel,
} from '../../types/levels';
import { apiQueryFn } from '../utils';
import { LevelResolvedRecord } from '~/util/types/submissions';

export type userProfileQueryParams = {
    list: 'classic' | 'platformer';
    userid: string;
};

export const userProfileQuery = ({ list, userid }: userProfileQueryParams) =>
    queryOptions<ProfileMerged, ApiError>({
        queryKey: ['users', 'profile', userid, list],
        queryFn: async () => {
            const profile = await apiQueryFn<Profile>({
                path: `/${list === 'classic' ? 'aredl' : 'arepl'}/profile/${userid}`,
            })();

            const createdLevels = profile.created.map((level) => ({
                ...level,
                created: true,
                published: false,
            }));
            const publishedLevels = profile.published.map((level) => ({
                ...level,
                created: false,
                published: true,
            }));

            const mergedLevelsMap: Record<string, ProfileMergedLevel> = {};

            for (const level of [...createdLevels, ...publishedLevels]) {
                if (!mergedLevelsMap[level.id]) {
                    mergedLevelsMap[level.id] = level;
                } else {
                    mergedLevelsMap[level.id] = {
                        ...mergedLevelsMap[level.id],
                        created:
                            mergedLevelsMap[level.id].created || level.created,
                        published:
                            mergedLevelsMap[level.id].published ||
                            level.published,
                    };
                }
            }

            const mergedLevels = Object.values(mergedLevelsMap);
            profile.created = mergedLevels.sort(
                (a, b) => (a.position ?? Infinity) - (b.position ?? Infinity),
            );
            return profile as ProfileMerged;
        },
    });

//
// Country-related queries
//

export type countryProfileQueryParams = {
    list: 'classic' | 'platformer';
    country: string;
};

export const countryProfileQuery = ({
    list,
    country,
}: countryProfileQueryParams) =>
    queryOptions<CountryProfileMerged, ApiError>({
        queryKey: ['country', country, list, 'profile'],
        queryFn: async (): Promise<CountryProfileMerged> => {
            const profile = await apiQueryFn<CountryProfile>({
                path: `/${list === 'classic' ? 'aredl' : 'arepl'}/country/${country}`,
            })();

            const createdLevels = profile.created.map((level) => ({
                ...level,
                publisher: null,
            }));
            const publishedLevels = profile.published.map((level) => ({
                ...level,
                creators: [],
            }));

            const mergedLevelsMap: Record<
                string,
                CountryClanProfileMergedLevel
            > = {};

            for (const level of [...createdLevels, ...publishedLevels]) {
                if (!mergedLevelsMap[level.id]) {
                    mergedLevelsMap[level.id] = level;
                } else {
                    mergedLevelsMap[level.id] = {
                        ...mergedLevelsMap[level.id],
                        publisher:
                            mergedLevelsMap[level.id].publisher ||
                            level.publisher,
                        creators:
                            mergedLevelsMap[level.id].creators ||
                            level.creators,
                    };
                }
            }

            const mergedLevels = Object.values(mergedLevelsMap);
            profile.created = mergedLevels.sort(
                (a, b) => (a.position ?? Infinity) - (b.position ?? Infinity),
            );

            return profile as CountryProfileMerged;
        },
    });

export type countryLevelRecordsQueryType = {
    list: 'classic' | 'platformer';
    country: number;
    levelid: string;
};
export const countryLevelRecordsQuery = ({
    list,
    country,
    levelid,
}: countryLevelRecordsQueryType) =>
    queryOptions<LevelResolvedRecord[], ApiError>({
        queryKey: ['country', country, list, 'levelrecords', levelid],
        queryFn: apiQueryFn<LevelResolvedRecord[]>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/country/${country}/levels/${levelid}/records`,
        }),
    });

//
// Clan-related queries
//

export type clanProfileQueryParams = {
    list: 'classic' | 'platformer';
    clanid: string;
};

export const clanProfileQuery = ({ list, clanid }: clanProfileQueryParams) =>
    queryOptions<ClanProfileMerged, ApiError>({
        queryKey: ['clan', clanid, list, 'profile'],
        queryFn: async (): Promise<ClanProfileMerged> => {
            const profile = await apiQueryFn<ClanProfile>({
                path: `/${list === 'classic' ? 'aredl' : 'arepl'}/clan/${clanid}`,
            })();

            return profile as ClanProfileMerged;
        },
    });

export type clanLevelRecordsQueryType = {
    list: 'classic' | 'platformer';
    clanid: string;
    levelid: string;
};
export const clanLevelRecordsQuery = ({
    list,
    clanid,
    levelid,
}: clanLevelRecordsQueryType) =>
    queryOptions<LevelResolvedRecord[], ApiError>({
        queryKey: ['clan', clanid, list, 'levelrecords', levelid],
        queryFn: apiQueryFn<LevelResolvedRecord[]>({
            path: `/${list === 'classic' ? 'aredl' : 'arepl'}/clan/${clanid}/levels/${levelid}/records`,
        }),
    });
